import React, { useState, useEffect, useCallback } from "react";
import { authFetch } from "../pages/Auth";
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  COLORS, commonAxisStyle, commonGridStyle, tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

// ═══════════════════════════════════════════════════════════
// DATE RANGE FILTER
// ═══════════════════════════════════════════════════════════

const RANGES = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "6mo", days: 180 },
  { label: "1yr", days: 365 },
  { label: "All", days: null },
];

function DateRangeFilter({ selected, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
      {RANGES.map((r) => (
        <button key={r.label} onClick={() => onChange(r.days)}
          style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit", border: "1px solid",
            background: selected === r.days ? "rgba(0,229,255,0.1)" : "#131920",
            color: selected === r.days ? COLORS.primary : COLORS.muted,
            borderColor: selected === r.days ? "rgba(0,229,255,0.3)" : COLORS.border,
          }}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════

function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let c = false;
    setLoading(true);
    authFetch(path)
      .then((r) => r.json())
      .then((d) => { if (!c) setData(d); })
      .catch((e) => { if (!c) setError(e.message); })
      .finally(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, [path]);

  return { data, loading, error };
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`, borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function ChartCard({ title, subtitle, children, loading: l, error: e }) {
  return (
    <div style={cardStyle}>
      <div style={{ marginBottom: 16 }}>
        <div style={chartHeaderStyle}>{title}</div>
        {subtitle && <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: "monospace", letterSpacing: 1 }}>{subtitle}</div>}
      </div>
      {l ? <Spinner /> : e ? <div style={{ color: COLORS.danger, fontSize: 12, padding: 20, textAlign: "center" }}>{e}</div> : children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COHORT COMPARISON
// ═══════════════════════════════════════════════════════════

function CohortComparison() {
  const { data, loading, error } = useApi("/api/analytics/weight-trends");

  const trends = Array.isArray(data?.trends) ? data.trends : [];
  const chartData = [...trends].reverse().map((t) => ({
    month: typeof t.month === "string" ? t.month.slice(0, 7) : String(t.month ?? ""),
    patients: t.patients_measured ?? 0,
    avgWeight: t.avg_weight ?? 0,
  }));

  return (
    <ChartCard title="Enrollment Cohort Trends" subtitle="PATIENTS MEASURED PER MONTH" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No cohort data</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid {...commonGridStyle} vertical={false} />
            <XAxis dataKey="month" tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="patients" fill={COLORS.primary} radius={[4, 4, 0, 0]} name="Patients Measured" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

// ═══════════════════════════════════════════════════════════
// RISK TRENDS OVER TIME
// ═══════════════════════════════════════════════════════════

function RiskTrends() {
  const { data: summaryData, loading: l1 } = useApi("/api/analytics/summary");
  const { data: riskData, loading: l2 } = useApi("/api/ml/dropout-risk");

  const loading = l1 || l2;
  const summary = summaryData?.summary || {};
  const dist = riskData?.risk_distribution || {};

  const total = (dist.high || 0) + (dist.medium || 0) + (dist.low || 0);

  return (
    <ChartCard title="Risk Overview" subtitle="CURRENT POPULATION" loading={loading}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        {[
          { label: "High Risk", count: dist.high || 0, color: "#ff4d6d" },
          { label: "Medium Risk", count: dist.medium || 0, color: "#fbbf24" },
          { label: "Low Risk", count: dist.low || 0, color: "#34d399" },
        ].map((r) => (
          <div key={r.label} style={{
            padding: 14, borderRadius: 8, textAlign: "center",
            background: `${r.color}08`, border: `1px solid ${r.color}20`,
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: r.color }}>{r.count}</div>
            <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 4 }}>{r.label}</div>
            {total > 0 && (
              <div style={{ fontSize: 9, color: COLORS.muted }}>
                {((r.count / total) * 100).toFixed(0)}%
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: COLORS.muted, textAlign: "center" }}>
        Retention rate: <span style={{ color: COLORS.primary, fontWeight: 700 }}>
          {summary.retention_rate != null ? `${(summary.retention_rate * 100).toFixed(1)}%` : "\u2014"}
        </span> &middot; Total: {summary.total_patients || 0} patients
      </div>
    </ChartCard>
  );
}

// ═══════════════════════════════════════════════════════════
// ROI DEEP DIVE
// ═══════════════════════════════════════════════════════════

function ROIDeepDive() {
  const { data, loading, error } = useApi("/api/analytics/adherence");

  const adherence = Array.isArray(data?.adherence) ? data.adherence : [];
  const chartData = adherence.map((r, i) => ({
    program: (r.program_name || "Unknown").replace(/ \d+(\.\d+)?mg/, ""),
    patients: r.patient_count ?? 0,
    avgRefills: r.avg_refills ?? 0,
    pdc: Math.round((r.pdc_above_80_pct ?? 0) * 100),
    estSavings: Math.round((r.patient_count ?? 0) * (r.pdc_above_80_pct ?? 0) * 2800),
  }));

  const totalSavings = chartData.reduce((sum, r) => sum + r.estSavings, 0);

  return (
    <ChartCard title="ROI by Program" subtitle="ESTIMATED SAVINGS FROM ADHERENT PATIENTS" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No adherence data</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid {...commonGridStyle} vertical={false} />
              <XAxis dataKey="program" tick={commonAxisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v, name) => [name === "estSavings" ? `$${v.toLocaleString()}` : v, name === "estSavings" ? "Est. Savings" : name === "pdc" ? "PDC >=80%" : "Patients"]} />
              <Bar dataKey="estSavings" fill={COLORS.success} radius={[4, 4, 0, 0]} name="Est. Savings" />
            </BarChart>
          </ResponsiveContainer>
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 8,
            background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.15)",
            textAlign: "center",
          }}>
            <span style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1 }}>TOTAL ESTIMATED ANNUAL SAVINGS: </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: COLORS.success }}>${totalSavings.toLocaleString()}</span>
          </div>
        </>
      )}
    </ChartCard>
  );
}

// ═══════════════════════════════════════════════════════════
// EXPORT BUTTON
// ═══════════════════════════════════════════════════════════

function ExportButton() {
  const [exporting, setExporting] = useState(false);

  const exportCSV = useCallback(async () => {
    setExporting(true);
    try {
      const [summaryRes, adherenceRes, trendsRes] = await Promise.all([
        authFetch("/api/analytics/summary").then((r) => r.json()),
        authFetch("/api/analytics/adherence").then((r) => r.json()),
        authFetch("/api/analytics/weight-trends").then((r) => r.json()),
      ]);

      let csv = "Pathriva Analytics Export\n\n";
      csv += "SUMMARY\n";
      const s = summaryRes?.summary || {};
      csv += `Total Patients,${s.total_patients || 0}\n`;
      csv += `Active Patients,${s.active_patients || 0}\n`;
      csv += `Retention Rate,${s.retention_rate != null ? (s.retention_rate * 100).toFixed(1) + "%" : "N/A"}\n\n`;

      csv += "ADHERENCE BY PROGRAM\nProgram,Patients,Avg Refills,PDC >=80%\n";
      (adherenceRes?.adherence || []).forEach((r) => {
        csv += `${r.program_name},${r.patient_count},${r.avg_refills},${((r.pdc_above_80_pct || 0) * 100).toFixed(1)}%\n`;
      });

      csv += "\nWEIGHT TRENDS\nMonth,Patients Measured,Avg Weight,Min Weight,Max Weight\n";
      (trendsRes?.trends || []).forEach((t) => {
        csv += `${t.month},${t.patients_measured},${t.avg_weight},${t.min_weight},${t.max_weight}\n`;
      });

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pathriva_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silent fail — button just re-enables
    }
    setExporting(false);
  }, []);

  return (
    <button onClick={exportCSV} disabled={exporting} style={{
      background: "linear-gradient(135deg, #00e5ff, #0099cc)", border: "none",
      borderRadius: 8, color: "#000", padding: "8px 20px", fontSize: 12,
      fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: exporting ? 0.6 : 1,
    }}>
      {exporting ? "Exporting..." : "Export CSV"}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN ANALYTICS PAGE
// ═══════════════════════════════════════════════════════════

export default function AnalyticsPage() {
  const [dateRange, setDateRange] = useState(null);

  return (
    <div>
      <style>{`
        .ph-analytics-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
        @media(max-width:1024px){.ph-analytics-grid{grid-template-columns:1fr}}
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <DateRangeFilter selected={dateRange} onChange={setDateRange} />
        <ExportButton />
      </div>

      <div className="ph-analytics-grid">
        <CohortComparison />
        <RiskTrends />
      </div>

      <div className="ph-analytics-grid">
        <ROIDeepDive />
        <ChartCard title="Adherence Trend" subtitle="PDC RATE OVER TIME">
          <div style={{ color: COLORS.muted, fontSize: 12, padding: 30, textAlign: "center" }}>
            Adherence trend tracking will populate as more monthly data accumulates
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
